import { isOnLastPage } from './page-helpers';
import { getDomElement, getDomElements, getI18nByKey, makeHtml } from './utils';
import * as auth from './auth';

type NotificationAction = {
  text: string;
  href?: string;
};

type NotificationMessageOptions = {
  title?: string;
  message?: string;
  actions?: NotificationAction[];
};

type BadgeData = {
  badgesTotal: number;
  badgesExpired: number;
  badgesAwarded: number;
};

type CourseData = {
  name: string;
  badgesTotal: number;
  badgesExpired: number;
  badgesAwarded: number;
};

type IssueData = BadgeData;

type BadgeResponse = {
  issue?: IssueData;
  course?: CourseData;
};

type CourseBadgeCountResponse = {
  courses: number;
  badges: number;
}

// Global variable(s)
let hasVisitedLastPage = false;

/**
 * Fetches the number of unawarded course badges from the server.
 * @param {string} hostname - The server hostname.
 * @param {number} clientGroupID - The ID of the client group in context.
 * @returns {Promise<CourseBadgeCountResponse>} A promise that resolves to a response object holding the number of unawarded courses and badges.
 * @throws Will throw an error if the network response is not ok.
 */
const fetchUnawardedCourseBadgeCount = async (
  hostname: string,
  clientGroupID: number
): Promise<CourseBadgeCountResponse> => {
  if (!hostname) {
    throw new Error('Invalid parameters');
  }

  const response = await fetch(
    `https://${hostname}/api/Notifications/UnawardedCourseBadgeCount?ClientGroupID=${clientGroupID}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return (await response.json()) as CourseBadgeCountResponse;
};

/**
 * Fetches course issue badges from the server.
 */
const fetchCourseIssueBadges = async (
  hostname: string,
  clientGroupID: number,
  courseID: string,
  issueID: string
): Promise<BadgeResponse> => {
  if (!hostname || !clientGroupID || !courseID || !issueID) {
    throw new Error('Invalid parameters');
  }

  const response = await fetch(
    `https://${hostname}/api/MyAccount/CourseIssueBadges?ClientGroupID=${clientGroupID}&CourseID=${courseID}&IssueID=${issueID}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return (await response.json()) as BadgeResponse;
};

/**
 * Displays the notification count against the toolbar account icon.
 */
const showNotificationCount = (count: number = 0): void => {
  const linkAriaLabel = getI18nByKey(
    count === 1
      ? 'Notifications.MyAccount.NewItem.Single'
      : 'Notifications.MyAccount.NewItem.Multiple',
    `${count}`
  );

  const badgeHtml = makeHtml(`<div class="badge-count"><span>${count}</span></div>`);

  // Append a count badge alongside toolbar "Account" icon.
  getDomElements('#my-account').forEach(el => {
    el.setAttribute('aria-label', linkAriaLabel);
    el.appendChild(badgeHtml.cloneNode(true));
  });

  // Append a count badge alongside the "My Account" link.
  getDomElements('.my-account-link').forEach(el => {
    el.setAttribute('aria-label', linkAriaLabel);
    el.appendChild(badgeHtml.cloneNode(true));
  });
};

/**
 * Dismisses a notification by removing its closest parent element with the class 'notification'.
 * @param {Event} e - The event triggered by the user action.
 */
const dismissNotification = (e: Event): void => {
  const el = e.target as HTMLElement;
  const notification = el.closest('.notification');
  if (notification) notification.remove();
};

/**
 * Displays a notification with the given count and a link to the My Account page.
 * @param {number} count - The number of notifications.
 */
const showNotification = (pageTurn, count: number = 0): void => {
  const { hostV2, onlineMagIssueUrl } = pageTurn;
  const viewCoursesUrl = `https://${hostV2}/courses?redirecturl=${onlineMagIssueUrl}&documentguid=${window.documentGuid}`;
  const title = getI18nByKey(
    count === 1 ? 'Notifications.Courses.Title.Single' : 'Notifications.Courses.Title.Multiple',
    `${count}`
  );
  const message = getI18nByKey('Notifications.Courses.Message');

  const actions = [
    { text: getI18nByKey('Notifications.Actions.ViewCourses'), href: viewCoursesUrl }
  ];

  const notificationHtml = makeHtml(buildNotificationMessage({ title, message, actions }));

  getDomElements('#notifications').forEach(el => {
    el.innerHTML = '';
    el.appendChild(notificationHtml.cloneNode(true));
  });

  getDomElements('.dismiss-notification').forEach(el => {
    el.addEventListener('click', dismissNotification);
  });
};

/**
 * Shows course-specific notifications.
 * @param {PageTurn} pageTurn - The page turn data object.
 * @param {BadgeResponse} data - The badge data response.
 */
const showCourseNotifications = (pageTurn, data): void => {
  if (!data) return;

  const { courseID, hostV2, onlineMagIssueUrl } = pageTurn;
  const isLastPage = isOnLastPage(pageTurn);

  hasVisitedLastPage ||= isLastPage;

  const redirectUrl = new URL(onlineMagIssueUrl);
  redirectUrl.searchParams.set('CID', courseID);

  const myAccountCourseUrl = `https://${hostV2}/courses/${courseID}?redirecturl=${encodeURIComponent(
    redirectUrl.toString()
  )}&documentguid=${window.documentGuid}`;

  // Document Badges
  const issueBadgesTotal = data?.issue?.badgesTotal ?? 0;
  const issueBadgesExpired = data?.issue?.badgesExpired ?? 0;
  const issueBadgesAwarded = data?.issue?.badgesAwarded ?? 0;
  const issueBadgesRemaining =
    issueBadgesTotal - Math.max(0, issueBadgesAwarded - issueBadgesExpired);
  const issueBadgesEarned = issueBadgesTotal - issueBadgesRemaining;

  // Course Badges - Of which this document is part of
  const courseName = data?.course?.name ?? 'your course';
  const courseBadgesTotal = data?.course?.badgesTotal ?? 0;
  const courseBadgesExpired = data?.course?.badgesExpired ?? 0;
  const courseBadgesAwarded = data?.course?.badgesAwarded ?? 0;
  const courseBadgesRemaining =
    courseBadgesTotal - Math.max(0, courseBadgesAwarded - courseBadgesExpired);

  let title = '';
  let message = '';
  let actions = [];

  // However, if we have issue badges we'll reframe the messages as document
  if (pageTurn.badgesOnLastCheck !== null && !isLastPage) {
    if (
      pageTurn.badgeCountOnLastCheck !== -1 &&
      pageTurn.badgesOnLastCheck.badgesAwarded === issueBadgesAwarded &&
      pageTurn.badgesOnLastCheck.badgesExpired === issueBadgesExpired
    )
      return;
  }

  if (issueBadgesRemaining > 0) {
    if (issueBadgesEarned === 0) {
      if (issueBadgesTotal !== 1) {
        title = `You still have badges to earn`;
      } else {
        title = `You still have a badge to earn`;
      }
    } else if (issueBadgesEarned === 1) {
      title = `You earned a badge!`;
    } else {
      title = `You've earned ${issueBadgesEarned} badges!`;
    }

    if (issueBadgesRemaining === 1) {
      message = `There is one more badge to be earned in this document. Keep it up!`;
    } else {
      message = `There are ${issueBadgesRemaining} more badges to earn in this document. Keep it up!`;
    }
    actions = [{ text: 'Dismiss' }];

    // If this is the first time we're checking the badge count,
    // we'll show a different message
    if (pageTurn.badgeCountOnLastCheck === -1) {
      title = `Ready to get started?`;

      if (issueBadgesRemaining === 1) {
        message = `Earn one badge from this document to progress <strong>${courseName}</strong>.`;
      } else {
        message = `Earn ${issueBadgesRemaining} badges from this document to progress <strong>${courseName}</strong>.`;
      }

      actions = [{ text: "Let's go" }];
    }
  }

  if (issueBadgesRemaining === 0) {
    if (isLastPage) {
      title = `Document complete`;
    } else {
      title = `All badges earned`;
    }

    if (issueBadgesTotal === 1) {
      message = `You've earned all required badges in this document.`;
    } else {
      message = `You've earned all ${issueBadgesTotal} required badges in this document.`;
    }

    if (!hasVisitedLastPage) {
      message = `${message} However, there's still more to explore and read.`;
    }

    // Add info about the course if necessary here
    // Check course data first and set the baseline messaging
    if (courseBadgesRemaining > 0) {
      if (courseBadgesRemaining === 1) {
        message = `${message} There is one badge left to earn in this course.`;
      } else {
        message = `${message} There are ${courseBadgesRemaining} more badges left to earn in this course.`;
      }

      actions = [{ text: 'Return to course', href: myAccountCourseUrl }, { text: 'Dismiss' }];
    } else {
      title = `Course complete`;
      message = `${message} Your course <strong>${courseName}</strong> is now complete.`;
      actions = [{ text: 'Return to course', href: myAccountCourseUrl }, { text: 'Dismiss' }];
    }
  }

  pageTurn.badgeCountOnLastCheck = issueBadgesRemaining;
  pageTurn.badgesOnLastCheck = Object.assign({}, data.issue);

  getDomElement('#notifications').map(el => {
    el.innerHTML = '';
    el.appendChild(makeHtml(buildNotificationMessage({ title, message, actions })));
  });

  getDomElements('.dismiss-notification, .notification-action-dismiss').map(el => {
    el.addEventListener('click', e => dismissNotification(e));
  });
};

/**
 * Builds the HTML string for a notification notification message.
 * @param {NotificationMessageOptions} options - The options for the notification message.
 * @returns {string} The HTML string for the notification message.
 */
const buildNotificationMessage = ({
  title = '',
  message = '',
  actions = []
}: NotificationMessageOptions = {}): string => {
  return `
    <div 
      class="notification" 
      role="alert" 
      aria-labelledby="notification-title" 
      aria-describedby="notification-message" 
      aria-live="assertive" 
      tabindex="-1" 
    >
      <i class="icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" 
            stroke="currentColor" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round" 
            class="tabler-icon tabler-icon-message"
          >
              <path d="M8 9h8"></path>
              <path d="M8 13h6"></path>
              <path d="M18 4a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-5l-5 3v-3h-2a3 3 0 0 1 -3 -3v-8a3 3 0 0 1 3 -3h12z"></path>
          </svg>
      </i>
      <div class="main">
          <div class="title-wrapper">
              <span class="title" id="notification-title">${title}</span>
              <button type="button" class="dismiss-notification" aria-label="Dismiss">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    stroke-width="2" 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    class="tabler-icon tabler-icon-x"
                  >
                    <path d="M18 6l-12 12"></path><path d="M6 6l12 12"></path>
                </svg>                            
              </button>
          </div>
          <p class="message" id="notification-message">
            ${message}
          </p>
          <div class="notification-actions">
            ${buildNotificationActions(actions)}
          </div>
      </div>
    </div>
  `;
};

/**
 * Builds the HTML string for the actions in a notification notification.
 * @param {NotificationAction[]} actions - The array of actions to include in the notification.
 * @returns {string} The HTML string for the notification actions.
 */
const buildNotificationActions = (actions: NotificationAction[]): string => {
  return actions
    .map(action => {
      const { href, text } = action;

      if (href) {
        return `<a href="${href}" class="notification-action">
                    <div class="inner">
                        <div class="label">${text}</div>
                    </div>
                </a>`;
      } else {
        return `<button type="button" class="notification-action notification-action-dismiss">
                    <div class="inner">
                        <div class="label">${text}</div>
                    </div>
                </button>`;
      }
    })
    .join('');
};

/**
 * Fetches and displays notifications based on the current page context.
 * @returns {Promise<void>}
 */
const getNotifications = async ({ courseOnly = false } = {}): Promise<void> => {
  try {
    const pageTurn = await window.ptiGetInstance();
    const { hostV2, onlineMagIssueID, clientGroupID, courseID, config } = pageTurn;
    const hasMyAccountV2 = config?.features?.MyAccountV2 || false;
    const authPathSection = auth.getAuthPath(pageTurn.auth);
    const hasAuthRedirect = Boolean(authPathSection);

    if (hasMyAccountV2 && hasAuthRedirect) {
      if (onlineMagIssueID && courseID) {
        const data = await fetchCourseIssueBadges(
          hostV2,
          clientGroupID,
          courseID,
          onlineMagIssueID
        );
        showCourseNotifications(pageTurn, data);
      } else {
        if (courseOnly) return;

        const { courses } = await fetchUnawardedCourseBadgeCount(hostV2, clientGroupID);

        if (courses > 0) {
          showNotificationCount(courses);
          showNotification(pageTurn, courses);
        }
      }
    }
  } catch (error) {
    console.error('Failed to get notifications', error);
  }
};

/**
 * Dismisses all notifications by removing them from the DOM.
 */
const autoDismissNotification = () => {
  getDomElements('#notifications .notification').forEach(el => {
    el.remove();
  });
};

export { getNotifications, autoDismissNotification };
